import { Fragment, useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, AccordionSummary, FormGroup } from "@mui/material";
import { LocaleContext } from "../../../routing/LangRouter";
import { saveTicketSelectedFilters } from "../../../store/actions";

//import constants
import { IS_MOBILE } from "../../../constants/breakpoints";

//import general components
import { BaseButton } from "../../common/base-button/base-button";
import BaseFilter from "../../common/base-filter/base-filter";
import FilterRangeSlider from "../../common/base-filter/filter-range-slider";

//import images and icons
import { ArrowDown2 } from "iconsax-react";

function BusFilter({ dispatch, filterListData, usedFilters, open, setOpen, setRequestFlag, setUid }) {
    //general variables
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //sessionStorage variables
    const busInformation = JSON.parse(sessionStorage.getItem("busInformation"));
    const [filterList, setFilterList] = useState(filterListData);

    useEffect(() => {
        setFilterList(filterListData);
    }, [filterListData]);

    //category names for filters
    const categoriesName = {
        prices: t("price"),
        move_times: t("General.moveTime"),
    };

    //define categories for range filters and option filters
    const rangeFilterCategories = ["prices", "move_times"];

    //handle changes in filter selection
    const [selectedFilters, setSelectedFilters] = useState({});
    const activeFilterCount = Object.keys(selectedFilters).length || "";

    //range slider filters//
    //define variables for range slider filters
    const [rangeValues, setRangeValues] = useState(
        rangeFilterCategories?.reduce((map, obj) => {
            map[obj] = [0, filterList?.[obj]?.length - 1];
            return map;
        }, {}),
    );

    //handle change to the range slider for each filter
    const handleRangeChange = (event, newValue, filterName) => {
        setRangeValues(values => ({ ...values, [filterName]: newValue }));
    };

    //handle change to the range slider on mouse up and send the filtes to the parent component
    const handleRangeCommited = (event, _, filterName, from, to) => {
        const updatedFilters = { ...selectedFilters, [filterName]: [from, to] };
        setSelectedFilters(updatedFilters);
    };

    //this function handles the submission of filters on a mobile device.
    const handleSubmitFilter = () => {
        if (selectedFilters) {
            dispatch(saveTicketSelectedFilters(formatFiltersForSubmission(selectedFilters)));
            setRequestFlag(true);
        }
        setOpen(false);
    };

    // This function formats the selected filters for submission by converting the input object into a result object
    // where each category contains a comma-separated string of selected keys
    const formatFiltersForSubmission = input => {
        return Object.entries(input).reduce((result, [category, value]) => {
            result[category] = value;
            return result;
        }, {});
    };

    //set default values for selected filters based on the provided defaultValue prop
    useEffect(() => {
        if (!usedFilters) return;

        const updatedFilters = Object.entries(usedFilters).reduce((acc, [filterName, filterValues]) => {
            if (rangeFilterCategories?.includes(filterName) && !!filterValues?.[0] && !!filterValues?.[1]) {
                setRangeValues(values => ({
                    ...values,
                    [filterName]: filterValues.map(value => filterList?.[filterName]?.findIndex(item => item === value)),
                }));
                acc[filterName] = filterValues;
            }

            return acc;
        }, {});

        setSelectedFilters(updatedFilters);
    }, [usedFilters]);

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
        setRequestFlag(true);
        setOpen(false);
    };

    return (
        <>
            <BaseFilter
                open={open}
                setOpen={setOpen}
                handleSubmitFilter={handleSubmitFilter}
                resetFilters={resetFilters}
                activeFilterCount={activeFilterCount}
                top="0"
            >
                {filterList &&
                    Object.keys(filterList).map((filterKey, index) => {
                        return (
                            <Fragment key={index}>
                                {filterList?.[filterKey]?.length ? (
                                    <Accordion
                                        key={index}
                                        sx={{
                                            width: "100%",
                                            boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.25)",
                                            margin: " 0 !important",
                                        }}
                                        defaultExpanded
                                    >
                                        <AccordionSummary expandIcon={<ArrowDown2 color="var(--gray-color)" />} sx={{ fontWeight: "700" }}>
                                            {categoriesName?.[filterKey]}
                                        </AccordionSummary>

                                        <AccordionDetails
                                            sx={{ maxHeight: "20rem", overflowY: "scroll", "&::-webkit-scrollbar": { display: "none" } }}
                                        >
                                            {rangeFilterCategories?.includes(filterKey) && (
                                                <FilterRangeSlider
                                                    filterList={filterList}
                                                    filterName={filterKey}
                                                    min={0}
                                                    max={filterList?.[filterKey].length - 1}
                                                    value={rangeValues[filterKey]}
                                                    handleChange={handleRangeChange}
                                                    handleChangeCommited={handleRangeCommited}
                                                />
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                ) : null}
                            </Fragment>
                        );
                    })}
            </BaseFilter>
            {!IS_MOBILE && filterList && (
                <BaseButton text={t("General.Apply Filter")} color="primary" size="medium" radius="radius-10" handleClick={handleSubmitFilter} />
            )}
        </>
    );
}

export default connect()(BusFilter);
